import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { Box, Button, Card, CardContent, IconButton } from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import MUIDataTable, {
  MUIDataTableColumnOptions,
  MUIDataTableMeta,
  SelectableRows
} from 'mui-datatables';
import moment from 'moment';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import { RouteComponentProps } from 'react-router';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { IReferentialService } from '../../../services/Interfaces/IReferentialService';
import Referentials from '../../../helpers/Referentials.json';
import { ReferentialCode } from '../../../helpers/Constants';
import { WorkflowFormButtonStatus } from '../../../interfaces/Workflow';
import { IWorkflowService } from '../../../services/Interfaces/IWorkflowService';
import { IReferential } from '../../../interfaces/IReferential';
import DeleteOutlineTwoToneIcon from '@material-ui/icons/DeleteOutlineTwoTone';

interface IWorkflowFormButtonStatusesState {
  items: WorkflowFormButtonStatus[];
  isLoading: boolean;
  caseStatusTypeList: IReferential[];
  selectedCaseStatus: IReferential | null;
}

export interface ExternalFormButtonStatusesProps {
  workflowFormId: number;
}

type IProps = ExternalFormButtonStatusesProps & {
  appState: AppState;
} & ProviderContext &
  RouteComponentProps<{ id: string; workflowId: string }>;

class WorkflowFormButtonStatuses extends React.PureComponent<
  IProps,
  IWorkflowFormButtonStatusesState
> {
  private translatorService!: ITranslatorService;
  private workflowService!: IWorkflowService;
  private appReferentialService!: IReferentialService;

  static contextType = ApplicationContext;

  state = {
    items: [],
    isLoading: false,
    caseStatusTypeList: [],
    selectedCaseStatus: null
  } as IWorkflowFormButtonStatusesState;

  public componentDidMount() {
    this.setState({ isLoading: true }, async () => {
      await this.loadData();
    });
  }

  loadData = async () => {
    const ref = Referentials.referential.find((item) => item.code === ReferentialCode.CaseStatus);

    const [items, caseStatusTypes] = await Promise.all([
      this.workflowService.GetWorkflowFormButtonStatuses(this.props.workflowFormId),
      this.appReferentialService.Get(ref!.baseUrl)
    ]);

    items.forEach((item) => {
      const caseStatus = caseStatusTypes.find((s) => s.id === item.caseStatusId);
      item.caseStatus = caseStatus ?? null;
    });

    this.setState({
      items,
      caseStatusTypeList: caseStatusTypes.filter((s) => s.isActive === true),
      isLoading: false
    });
  };

  deleteItem = async (event: any, tableMeta: MUIDataTableMeta) => {
    try {
      const index = this.state.items.findIndex((i) => i.id === tableMeta.rowData[0]);
      const deleted = this.state.items[index];
      const newItems = [...this.state.items];
      newItems.splice(index, 1);

      await this.workflowService.RemoveWorkflowFormButtonStatus(deleted.id);

      this.setState({ items: newItems });

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  addItem = async () => {
    try {
      if (
        this.state.items.findIndex(
          (item) => item.caseStatusId === this.state.selectedCaseStatus!.id
        ) !== -1
      ) {
        this.props.enqueueSnackbar(
          this.translatorService.Tranlate(
            'ERROR_MSG_WORKFLOW_FORM_BUTTON_STATUS_EXISTS',
            'Statusul exista deja!'
          ),
          { variant: 'error' }
        );
        return;
      }

      await this.workflowService.AddWorkflowFormButtonStatus({
        caseStatusId: this.state.selectedCaseStatus!.id,
        workflowSectionStepFormId: this.props.workflowFormId,
        caseStatus: null,
        id: 0
      } as WorkflowFormButtonStatus);

      this.setState({ isLoading: true, selectedCaseStatus: null }, () => {
        this.loadData();
      });

      this.props.enqueueSnackbar(this.translatorService.Tranlate('SUCCES_MSG', 'OK'), {
        variant: 'success'
      });
    } catch (error) {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    }
  };

  getColumns = () => {
    return [
      {
        name: 'id',
        options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
      },
      {
        name: 'caseStatus',
        label: this.translatorService.Tranlate(
          'WORKFLOW_FORM_BUTTON_STATUS_CASE_STATUS_HEADER',
          'Status Dosar'
        ),
        options: {
          sort: false,
          customBodyRender: (value: IReferential) => (value ? value.displayName : '')
        } as MUIDataTableColumnOptions
      },
      {
        name: 'dateModification',
        label: this.translatorService.Tranlate(
          'WORKFLOW_PERMISSION_TAB_DATE_MODIFICATION_HEADER',
          'Data Modificare'
        ),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value: Date) =>
            moment.utc(value).local().toDate().toLocaleString(this.props.appState.language)
        } as MUIDataTableColumnOptions
      },
      {
        name: '',
        options: {
          filter: false,
          setCellHeaderProps: () => ({ align: 'center' }),
          setCellProps: () => ({ align: 'center' }),
          customBodyRender: (value: boolean, tableMeta: MUIDataTableMeta) => {
            return (
              <IconButton
                aria-label="delete"
                color="inherit"
                className="text-danger"
                size="small"
                onClick={(e) => this.deleteItem(e, tableMeta)}
              >
                <DeleteOutlineTwoToneIcon />
              </IconButton>
            );
          }
        }
      }
    ];
  };

  renderAddForm() {
    return (
      <Box mt={1} pt={1}>
        <Card elevation={1}>
          <CardContent>
            <ValidatorForm onSubmit={this.addItem}>
              <Autocomplete
                id="caseStatus"
                fullWidth
                className="m-2"
                options={this.state.caseStatusTypeList.sort((a, b) =>
                  a.displayName.localeCompare(b.displayName)
                )}
                value={this.state.selectedCaseStatus}
                onChange={(e: any, newValue: IReferential | null) =>
                  this.setState({ selectedCaseStatus: newValue })
                }
                getOptionLabel={(option: IReferential) => option.displayName || ''}
                renderInput={(params) => (
                  <TextValidator
                    {...params}
                    name="caseStatus"
                    value={this.state.selectedCaseStatus}
                    label={this.translatorService.Tranlate(
                      'WORKFLOW_FORM_BUTTON_STATUS_CASE_STATUS_HEADER',
                      'Status Dosar'
                    )}
                    fullWidth
                    validators={['required']}
                    errorMessages={[
                      this.translatorService.Tranlate(
                        'VALIDATORS_REQUIRED',
                        'Campul este obligatoriu'
                      )
                    ]}
                  />
                )}
              />
              <div className="text-right">
                <Button className="m-2" variant="contained" color="secondary" type="submit">
                  {this.translatorService.Tranlate('ADMIN_EDIT_USER_ROLES_BTN_ADD', 'ADAUGA')}
                </Button>
              </div>
            </ValidatorForm>
          </CardContent>
        </Card>
      </Box>
    );
  }

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.workflowService = (this.context as AppContext).workflowService;
    this.appReferentialService = (this.context as AppContext).referentialService;

    const options = {
      filter: true,
      search: true,
      viewColumns: false,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      elevation: 1
    };

    return (
      <Fragment>
        {this.renderAddForm()}
        <Box mt={1} pt={1}>
          <div className="d-flex flex-row text-center flex-wrap justify-content-center">
            <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
          </div>
          {!this.state.isLoading ? (
            <MUIDataTable
              title={''}
              data={this.state.items}
              columns={this.getColumns()}
              options={options}
            />
          ) : null}
        </Box>
      </Fragment>
    );
  }
}

const mergeProps = (
  stateProps: any,
  dispatchProps: any,
  ownProps: ExternalFormButtonStatusesProps
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps
});

export default connect(
  (state: ApplicationState) => ({ appState: state.app }),
  null,
  mergeProps
)(withSnackbar(WorkflowFormButtonStatuses as any));
